'use client';

import React, { useEffect } from 'react';

import usePageState from '@/hooks/usePageState';

import { ErrorOverlay } from '@/components/ErrorOverlay';
import { useGlobalConfig } from '@/components/GlobalContext';

import { useApp } from '@/app/frontdoor/AppContext';
import { CraveErrorCode } from '@/enums';
import { getSystem } from '@/system';
import { CraveError } from '@/types';
import { getCraveError } from '@/utils/errors';
import { getLocalization } from '@/utils/localization';

type NextErrorProps = {
  error: Error | CraveError;
  reset?: () => void;
};

function isCraveError(error: Error | CraveError): boolean {
  return (
    typeof (error as CraveError).code !== 'undefined' &&
    Object.values(CraveErrorCode).includes((error as CraveError).code)
  );
}
export default function Error({ error, reset }: NextErrorProps) {
  const { userLanguage } = useGlobalConfig();
  const { homeNavId } = useApp();
  const { replacePageState } = usePageState();
  const system = getSystem();

  useEffect(() => {
    if (system.logError) {
      system.logError(
        'other',
        'GENERIC_ERROR_4002',
        (error as Error).message || 'An unexpected error occurred',
        true
      );
    }
  }, [error, system]);

  let craveError: CraveError;
  if (isCraveError(error)) {
    craveError = error as CraveError;
  } else {
    craveError = getCraveError(CraveErrorCode.GENERIC_ERROR_4002, userLanguage);
  }

  let buttonLabel = getLocalization(userLanguage)['errors']['error-button-ok'];
  if (craveError?.retry) {
    buttonLabel = getLocalization(userLanguage)['errors']['error-button-retry'];
  }

  if (craveError.code === CraveErrorCode.NO_NETWORK_CONNECTION_4001) {
    buttonLabel = '';
  }

  const onKeyPress = () => {
    if (craveError?.retry) {
      if (!window.navigator.onLine) return;
      reset?.();
      return;
    }

    // homeNavId comes from the app config; without it, reload from the splash route.
    if (!homeNavId) {
      window.location.href = '/';
      return;
    }

    replacePageState(`/screen?id=${encodeURIComponent(homeNavId)}`);
  };

  return (
    <div data-testid="error-boundary-container">
      {craveError ? (
        <ErrorOverlay
          error={craveError}
          buttonLabel={buttonLabel}
          onKeyPress={onKeyPress}
        />
      ) : null}
    </div>
  );
}
